import { createSlice, current, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { rootReducer } from '@verantos-dev/app-state-base';
import {
  type ActiveCohortBuilderItemLegacy,
  type ActiveIndexDateItemLegacy,
  type CohortBuilderDefinitionLegacy,
  CohortBuilderItemChangedBy,
  CohortBuilderItemChangeStatusLegacy,
  CohortBuilderModalLegacy,
  DerivedEventInputFieldTypeLegacy,
  type DerivedEventLegacy,
  DerivedEventTypeLegacy,
  type FilterLegacy,
  FilterTypeLegacy,
  type IndexDateRangeLegacy,
  type IndexDateEvent,
  IndexDateTypeLegacy,
  type IndexDateVariableLegacy,
  type SimpleEventLegacy,
  SimpleEventType,
  SimpleEventTypeLegacy,
  type StudyPeriodRestrictionsLegacy,
  type SubjectVariableLegacy,
  SubjectVariableTypeLegacy,
  type TimeWindow,
} from '@verantos-dev/types-cohorts';
import {
  ConceptGroup,
  type LegacyConceptGroup,
  type ConceptGroupsConfidence,
  type ConceptGroupSelection,
} from '@verantos-dev/types-concepts';

import {
  setConceptGroupSelectionsReducer,
  optimisticCgsReportedUpdateReducer,
  markConceptGroupSelectionsAsReportedReducer,
} from './concept-groups/slice';
import { COHORT_BUILDER_SLICE_KEY } from './constants/constants';
import { recursiveCheckNextActiveCohortBuilderItemState } from './runtime/getNextActiveCohortBuilderItemState';
import {
  CohortBuilderSlice,
  CohortBuilderState,
  CohortBuilderWorkflow,
  QueuedEventModalSelection,
} from './types';
import { addChangeStatusToCohortBuilderItems } from './utils/addChangeStatusToCohortBuilderItems';
import {
  getFullCohortDslForDeleteDerivedEvent,
  getFullCohortDslForDerivedEvent,
} from './utils/dsl-generators/getFullCohortDslForDerivedEvent';
import {
  getFullCohortDslForIndexDate,
  getFullCohortDslForDeleteIndexDate,
} from './utils/dsl-generators/getFullCohortDslForIndexDate';
import {
  getFullCohortDslForDeleteSimpleEvent,
  getFullCohortDslForSimpleEvent,
} from './utils/dsl-generators/getFullCohortDslForSimpleEvent';
import { getFullCohortDslForStudyPeriodRestriction } from './utils/dsl-generators/getFullCohortDslForStudyPeriodRestriction';
import {
  getFullCohortDslForDeleteSubjectsVariables,
  getFullCohortDslForSubjectsVariables,
} from './utils/dsl-generators/getFullCohortDslForSubjectsVariables';
import { getCohortBuilderItemForEditModalById } from './utils/getCohortBuilderItemForEditModalById';
import { getCohortBuilderItemNameAndIdMaps } from './utils/getCohortBuilderItemNameAndIdMaps';
import { getInUseCohortBuilderItemsMap } from './utils/getInUseCohortBuilderItemsMap';
import { getNewCohortBuilderEvent } from './utils/getNewCohortBuilderEvent';
import { getNextCohortBuilderWorkflowForViewedItem } from './utils/getNextCohortBuilderWorkflowForViewedItem';
import { getNextFilterForInputFieldValueUpdate } from './utils/getNextFilterForInputFieldValueUpdate';
import { validateCohortDefinitionForProcessing } from './utils/validateCohortDefinitionForProcessing';
import { validateCohortDefinitionForSave } from './utils/validateCohortDefinitionForSave';

const initialState: CohortBuilderSlice = {
  cohortDefinition: undefined,
  cohortBuilderWorkflow: undefined,
  activeCohortBuilderItem: undefined,
  conceptGroups: undefined,
  // isLoggedInUserCohortOwner: false,
  isCohortUpdatedByBuilder: false,
  featureFlags: {},
  conceptGroupsConfidence: [],
  conceptGroupSelections: undefined,
  queuedEventModals: undefined,
  queuedEventModalsConceptGroupId: undefined,
  timeWindow: null,
  hasStagedChanges: false,
};

export const cohortBuilderSlice = createSlice({
  name: COHORT_BUILDER_SLICE_KEY,
  initialState,
  reducers: {
    /**
     * Reset entire cohort builder slice
     * Sets the cohort definition, cohort builder workflow, and active cohort builder item to undefined
     * @usage -- use this when user exits the cohort builder page
     */
    resetCohortBuilderSlice() {
      return { ...initialState };
    },

    /**
     * Top level slice reducers
     * These reducers are used to set the cohort definition, cohort builder workflow, and active cohort builder item
     *
     * @usage use these when user enters the cohort builder page and we need to initially set the state
     */

    /** Set cohort definition and cohort builder workflow state when entering the page */
    setInitialCohortBuilderSliceState(
      state: CohortBuilderSlice,
      action: PayloadAction<CohortBuilderDefinitionLegacy | undefined>
    ) {
      return {
        ...state,
        cohortDefinition: action.payload,
      };
    },

    /** Update the entire cohort definition */
    updateCohortBuilderSliceState(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        cohortDefinition: CohortBuilderDefinitionLegacy;
        clearOldStatus: boolean;
        changedBy: CohortBuilderItemChangedBy;
      }>
    ) {
      // calculate the CohortBuilderItemChangeStatus of each index date, subject variable, simple event, and derived event

      const { cohortDefinitionWithStatuses: nextActiveCohortBuilderState, updatedCohortItems } =
        addChangeStatusToCohortBuilderItems(
          state.cohortDefinition,
          action.payload.cohortDefinition,
          action.payload.clearOldStatus,
          action.payload.changedBy
        );
      const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;

      return {
        ...state,
        cohortDefinition: nextActiveCohortBuilderState,
        cohortBuilderWorkflow: {
          ...state.cohortBuilderWorkflow,
          // actionModal: currentCohortBuilderWorkflow?.actionModal ?? undefined,
          cohortBuilderItemIdsToNamesMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemIdsToNamesMap ?? {},
          cohortBuilderItemNamesToIdsMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemNamesToIdsMap ?? {},
          inUseCohortBuilderItemsMap:
            currentCohortBuilderWorkflow?.inUseCohortBuilderItemsMap ?? {},
          hasUnsavedChanged: validateCohortDefinitionForSave(nextActiveCohortBuilderState),
          isProcessEnabled: validateCohortDefinitionForProcessing(nextActiveCohortBuilderState),
          modal: currentCohortBuilderWorkflow?.modal ?? undefined,
          // activeStep: currentCohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,

          // filter out any items that have been updated
          viewedCohortBuilderItems: Object.keys(
            currentCohortBuilderWorkflow?.viewedCohortBuilderItems ?? {}
          ).reduce((nextViewedCohortBuilderItems: Record<string, boolean>, id) => {
            if (!updatedCohortItems.includes(id)) {
              nextViewedCohortBuilderItems[id] =
                currentCohortBuilderWorkflow?.viewedCohortBuilderItems?.[id] ?? false;
            }
            return nextViewedCohortBuilderItems;
          }, {}),
        },
      };
    },

    /**
     * Concept Groups Reducer
     */
    setConceptGroups(
      state: CohortBuilderSlice,
      action: PayloadAction<LegacyConceptGroup[] | ConceptGroup[] | undefined>
    ) {
      const newConceptGroups = action.payload;
      let nextActiveCohortBuilderItem = state.activeCohortBuilderItem;

      if (
        state.activeCohortBuilderItem &&
        state.cohortBuilderWorkflow?.modal &&
        state.cohortDefinition
      ) {
        // Re-calculate activeCohortBuilderItem with the new concept groups
        // This ensures its inputFieldConfigMap (and thus ComboBox options) are updated
        const currentModalType = state.cohortBuilderWorkflow.modal;
        const loadedActiveCohortBuilderItem = state.activeCohortBuilderItem; // already loaded

        nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
          currentModalType,
          state.cohortDefinition,
          loadedActiveCohortBuilderItem, // Use the currently active item
          {
            activeCohortBuilderItem: loadedActiveCohortBuilderItem,
            conceptGroups: newConceptGroups, // Use the new concept groups
            currentCohortDefinition: state.cohortDefinition,
            featureFlags: state.featureFlags,
            derivedEventType:
              currentModalType &&
              [
                CohortBuilderModalLegacy.ADD_DERIVED_EVENT,
                CohortBuilderModalLegacy.EDIT_DERIVED_EVENT,
              ].includes(currentModalType)
                ? (loadedActiveCohortBuilderItem.type as DerivedEventTypeLegacy)
                : undefined,
          }
        );
      }

      return {
        ...state,
        conceptGroups: newConceptGroups,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    /**
     * Cohort Builder Definition Reducers
     * These reducers are used to set the cohort definition state
     *
     * @usage use these when user saves a change to a cohort builder
     *         item by clicking the save button in the edit modal
     */

    /** Update the cohort definition */
    setIndexDate(state: CohortBuilderSlice, action: PayloadAction<IndexDateVariableLegacy>) {
      const { cohortDefinition } = getFullCohortDslForIndexDate(
        state.cohortDefinition,
        action.payload,
        true
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          subjectVariables: cohortDefinition?.subjectVariables,
          indexDate: cohortDefinition?.indexDate,
          simpleEvents: cohortDefinition?.simpleEvents,
          derivedEvents: cohortDefinition?.derivedEvents,
          // FEATURE FLAG: Preserve analytical variables fields when present
          // These fields come from backend when analytical_variables_in_cohort_builder is enabled
          // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: state.cohortDefinition?.criteria,
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
        hasStagedChanges: true,
      };
    },
    deleteIndexDate(state: CohortBuilderSlice) {
      const { cohortDefinition } = getFullCohortDslForDeleteIndexDate(state.cohortDefinition);

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          subjectVariables: cohortDefinition?.subjectVariables ?? [],
          indexDate: cohortDefinition?.indexDate,
          simpleEvents: cohortDefinition?.simpleEvents ?? [],
          derivedEvents: cohortDefinition?.derivedEvents ?? [],
          // FEATURE FLAG: Preserve analytical variables fields when present
          // These fields come from backend when analytical_variables_in_cohort_builder is enabled
          // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: state.cohortDefinition?.criteria,
        },
        hasStagedChanges: true,
      };
    },

    /** Subject Variable Reducers */
    setSubjectVariable(state: CohortBuilderSlice, action: PayloadAction<SubjectVariableLegacy>) {
      const { cohortDefinition } = getFullCohortDslForSubjectsVariables(
        state.cohortDefinition,
        action.payload,
        true
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          subjectVariables: cohortDefinition?.subjectVariables,
          indexDate: cohortDefinition?.indexDate,
          simpleEvents: cohortDefinition?.simpleEvents,
          derivedEvents: cohortDefinition?.derivedEvents,
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: cohortDefinition?.criteria,
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
        hasStagedChanges: true,
      };
    },
    deleteSubjectVariable(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const { cohortDefinition } = getFullCohortDslForDeleteSubjectsVariables(
        state.cohortDefinition,
        action.payload
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          indexDate: cohortDefinition?.indexDate,
          subjectVariables: cohortDefinition?.subjectVariables ?? [],
          simpleEvents: cohortDefinition?.simpleEvents ?? [],
          derivedEvents: cohortDefinition?.derivedEvents ?? [],
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: cohortDefinition?.criteria,
        },
        hasStagedChanges: true,
      };
    },
    /** Simple Event Reducers */
    setSimpleEvent(state: CohortBuilderSlice, action: PayloadAction<SimpleEventLegacy>) {
      const { cohortDefinition } = getFullCohortDslForSimpleEvent(
        state.cohortDefinition,
        action.payload,
        true
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          subjectVariables: cohortDefinition?.subjectVariables,
          indexDate: cohortDefinition?.indexDate,
          simpleEvents: cohortDefinition?.simpleEvents,
          derivedEvents: cohortDefinition?.derivedEvents,
          // FEATURE FLAG: Preserve analytical variables fields when present
          // These fields come from backend when analytical_variables_in_cohort_builder is enabled
          // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: state.cohortDefinition?.criteria,
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
        hasStagedChanges: true,
      };
    },
    deleteSimpleEvent(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const { cohortDefinition } = getFullCohortDslForDeleteSimpleEvent(
        state.cohortDefinition,
        action.payload
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          indexDate: cohortDefinition?.indexDate,
          subjectVariables: cohortDefinition?.subjectVariables ?? [],
          simpleEvents: cohortDefinition?.simpleEvents ?? [],
          derivedEvents: cohortDefinition?.derivedEvents ?? [],
          // FEATURE FLAG: Preserve analytical variables fields when present
          // These fields come from backend when analytical_variables_in_cohort_builder is enabled
          // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: state.cohortDefinition?.criteria,
        },
        hasStagedChanges: true,
      };
    },

    /** Derived Event Reducers */
    setDerivedEvent(state: CohortBuilderSlice, action: PayloadAction<DerivedEventLegacy>) {
      const { cohortDefinition } = getFullCohortDslForDerivedEvent(
        state.cohortDefinition,
        action.payload,
        true
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          subjectVariables: cohortDefinition?.subjectVariables,
          indexDate: cohortDefinition?.indexDate,
          simpleEvents: cohortDefinition?.simpleEvents,
          derivedEvents: cohortDefinition?.derivedEvents,
          // FEATURE FLAG: Preserve analytical variables fields when present
          // These fields come from backend when analytical_variables_in_cohort_builder is enabled
          // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: state.cohortDefinition?.criteria,
        },
        cohortBuilderWorkflow: getNextCohortBuilderWorkflowForViewedItem(
          action.payload.id,
          true,
          state.cohortBuilderWorkflow,
          state.cohortDefinition
        ),
        hasStagedChanges: true,
      };
    },
    deleteDerivedEvent(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const { cohortDefinition } = getFullCohortDslForDeleteDerivedEvent(
        state.cohortDefinition,
        action.payload
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          indexDate: cohortDefinition?.indexDate,
          subjectVariables: cohortDefinition?.subjectVariables ?? [],
          simpleEvents: cohortDefinition?.simpleEvents ?? [],
          derivedEvents: cohortDefinition?.derivedEvents ?? [],
          // FEATURE FLAG: Preserve analytical variables fields when present
          // These fields come from backend when analytical_variables_in_cohort_builder is enabled
          // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: state.cohortDefinition?.criteria,
        },
        hasStagedChanges: true,
      };
    },

    setStudyPeriodRestrictions(
      state: CohortBuilderSlice,
      action: PayloadAction<StudyPeriodRestrictionsLegacy>
    ) {
      const { cohortDefinition } = getFullCohortDslForStudyPeriodRestriction(
        state.cohortDefinition,
        action.payload,
        true
      );

      return {
        ...state,
        cohortDefinition: {
          studyPeriodRestrictions: cohortDefinition?.studyPeriodRestrictions,
          subjectVariables: cohortDefinition?.subjectVariables,
          indexDate: cohortDefinition?.indexDate,
          simpleEvents: cohortDefinition?.simpleEvents,
          derivedEvents: cohortDefinition?.derivedEvents,
          // FEATURE FLAG: Preserve analytical variables fields when present
          // These fields come from backend when analytical_variables_in_cohort_builder is enabled
          // CLEANUP TODO: When removing the `analytical_variables_in_cohort_builder` feature flag, ensure these fields are always preserved
          analyticalVariables: state.cohortDefinition?.analyticalVariables,
          criteria: state.cohortDefinition?.criteria,
        },
        hasStagedChanges: true,
      };
    },

    /**
     * Cohort Builder Workflow Reducers
     *
     * These reducers are used to:
     * - manage the state of the cohort builder workflow
     * - manage the state of the cohort builder edit modal
     */

    markItemViewed(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;
      if (currentCohortBuilderWorkflow === undefined) {
        throw new Error('setActiveStep: Cohort Builder Workflow is undefined');
      }

      return {
        ...state,
        cohortBuilderWorkflow: {
          ...state.cohortBuilderWorkflow,
          // actionModal: currentCohortBuilderWorkflow?.actionModal ?? undefined,
          cohortBuilderItemIdsToNamesMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemIdsToNamesMap ?? {},
          cohortBuilderItemNamesToIdsMap:
            currentCohortBuilderWorkflow?.cohortBuilderItemNamesToIdsMap ?? {},
          inUseCohortBuilderItemsMap:
            currentCohortBuilderWorkflow?.inUseCohortBuilderItemsMap ?? {},
          hasUnsavedChanged: currentCohortBuilderWorkflow?.hasUnsavedChanged ?? false,
          isProcessEnabled: currentCohortBuilderWorkflow?.isProcessEnabled ?? false,
          modal: currentCohortBuilderWorkflow?.modal ?? undefined,
          // activeStep: currentCohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,
          viewedCohortBuilderItems: {
            ...state.cohortBuilderWorkflow?.viewedCohortBuilderItems,
            [action.payload]: true,
          },
        },
      };
    },

    // Set the active step in the workflow
    // setActiveStep(state: CohortBuilderSlice, action: PayloadAction<CohortBuilderStepLegacy>) {
    //   const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;
    //   if (currentCohortBuilderWorkflow === undefined) {
    //     throw new Error('setActiveStep: Cohort Builder Workflow is undefined');
    //   }

    //   return {
    //     ...state,
    //     cohortBuilderWorkflow: {
    //       ...currentCohortBuilderWorkflow,
    //       hasUnsavedChanged: currentCohortBuilderWorkflow?.hasUnsavedChanged ?? false,
    //       isProcessEnabled: currentCohortBuilderWorkflow?.isProcessEnabled ?? false,
    //       modal: currentCohortBuilderWorkflow?.modal ?? undefined,
    //       // activeStep: action.payload,
    //     },
    //   };
    // },

    // Set the modal to display
    setModal(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        modal: CohortBuilderModalLegacy;
      }>
    ) {
      const currentCohortBuilderWorkflow = current(state.cohortBuilderWorkflow);
      const currentCohortDefinition = current(state.cohortDefinition);
      if (currentCohortBuilderWorkflow === undefined) {
        throw new Error('setModal: Cohort Builder Workflow is undefined');
      }
      if (currentCohortDefinition === undefined) {
        throw new Error('setModal: Cohort Definition is undefined');
      }

      const loadedActiveCohortBuilderItem = getCohortBuilderItemForEditModalById(
        action.payload.id,
        action.payload.modal,
        currentCohortDefinition
      );
      const nextCohortBuilderWorkflow: CohortBuilderWorkflow = {
        ...currentCohortBuilderWorkflow,
        modal: action.payload.modal,
        hasUnsavedChanged: currentCohortBuilderWorkflow?.hasUnsavedChanged ?? false,
        isProcessEnabled: currentCohortBuilderWorkflow?.isProcessEnabled ?? false,
        // activeStep: currentCohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,
      };
      const currentModalType = nextCohortBuilderWorkflow?.modal;
      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        action.payload.modal,
        currentCohortDefinition,
        loadedActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: loadedActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition,
          featureFlags: state.featureFlags,
          derivedEventType:
            currentModalType &&
            [
              CohortBuilderModalLegacy.ADD_DERIVED_EVENT,
              CohortBuilderModalLegacy.EDIT_DERIVED_EVENT,
            ].includes(currentModalType)
              ? (loadedActiveCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );
      return {
        ...state,
        cohortBuilderWorkflow: nextCohortBuilderWorkflow,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    // clear the modal
    clearModal(state: CohortBuilderSlice) {
      if (state.cohortBuilderWorkflow === undefined) {
        throw new Error('clearModal: cohortBuilderWorkflow is undefined');
      }
      return {
        ...state,
        cohortBuilderWorkflow: {
          ...state.cohortBuilderWorkflow,
          modal: undefined,
        },
        activeCohortBuilderItem: undefined,
      };
    },

    // Set the modal to display
    // setActionModal(
    //   state: CohortBuilderSlice,
    //   action: PayloadAction<{
    //     id: string;
    //     modal: CohortActionModalLegacy;
    //   }>
    // ) {
    //   const currentCohortBuilderWorkflow = state.cohortBuilderWorkflow;
    //   const currentCohortDefinition = state.cohortDefinition;
    //   if (currentCohortBuilderWorkflow === undefined) {
    //     throw new Error('setActionModal: Cohort Builder Workflow is undefined');
    //   }
    //   if (currentCohortDefinition === undefined) {
    //     throw new Error('setActionModal: Cohort Definition is undefined');
    //   }

    //   return {
    //     ...state,
    //     cohortBuilderWorkflow: {
    //       ...currentCohortBuilderWorkflow,
    //       // actionModal: action.payload.modal,
    //     },
    //   };
    // },

    // clear the action modal
    // clearActionModal(state: CohortBuilderSlice) {
    //   if (state.cohortBuilderWorkflow === undefined) {
    //     throw new Error('clearActionModal: cohortBuilderWorkflow is undefined');
    //   }
    //   return {
    //     ...state,
    //     cohortBuilderWorkflow: {
    //       ...state.cohortBuilderWorkflow,
    //       actionModal: undefined,
    //     },
    //   };
    // },

    /**
     * Active Cohort Builder Item Reducers
     * These reducers are used to:
     * - manage the state of the active cohort builder item that is displayed in the edit modal
     *
     * @usage use these when the user is in the edit modal
     */

    // Set the active cohort builder item name
    setActiveCohortBuilderItemName(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        name: string;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      // If the name and type exist, then pass through the runtime
      if (activeCohortBuilderItem.name && activeCohortBuilderItem.type) {
        const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
          ...activeCohortBuilderItem,
          name: action.payload.name,
        };

        const currentModalType = state.cohortBuilderWorkflow?.modal;
        const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
          state.cohortBuilderWorkflow?.modal,
          state.cohortDefinition,
          currentActiveCohortBuilderItem,
          {
            activeCohortBuilderItem: currentActiveCohortBuilderItem,
            conceptGroups: state.conceptGroups,
            currentCohortDefinition: state.cohortDefinition,
            featureFlags: state.featureFlags,
            derivedEventType:
              currentModalType &&
              [
                CohortBuilderModalLegacy.ADD_DERIVED_EVENT,
                CohortBuilderModalLegacy.EDIT_DERIVED_EVENT,
              ].includes(currentModalType)
                ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
                : undefined,
          }
        );

        return {
          ...state,
          activeCohortBuilderItem: nextActiveCohortBuilderItem,
        };
      }

      return {
        ...state,
        activeCohortBuilderItem: { ...activeCohortBuilderItem, name: action.payload.name },
      };
    },

    // Set the active cohort builder item subject variable type
    setActiveCohortBuilderItemSubjectVariableType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: SubjectVariableTypeLegacy;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const hasTypeSelectionChanged = state.activeCohortBuilderItem?.type !== action.payload.type;

      // only pass through the runtime if the name and type exist
      if (activeCohortBuilderItem.name && hasTypeSelectionChanged) {
        const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
          ...activeCohortBuilderItem,
          type: action.payload.type,
          conceptFilters: [], // reset the concept filters
          numericFilters: [], // reset the numeric filters
          countFilter: undefined, // reset the count filter
        };

        const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
          state.cohortBuilderWorkflow?.modal,
          state.cohortDefinition,
          currentActiveCohortBuilderItem,
          {
            activeCohortBuilderItem: currentActiveCohortBuilderItem,
            conceptGroups: state.conceptGroups,
            currentCohortDefinition: state.cohortDefinition,
            derivedEventType: undefined,
            featureFlags: state.featureFlags,
          }
        );

        return {
          ...state,
          activeCohortBuilderItem: nextActiveCohortBuilderItem,
        };
      }

      return {
        ...state,
        activeCohortBuilderItem: {
          ...activeCohortBuilderItem,
          type: action.payload.type,
        },
      };
    },

    // Set the active cohort builder item simple event type
    setActiveCohortBuilderItemSimpleEventType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: SimpleEventTypeLegacy | SimpleEventType;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      if (activeCohortBuilderItem.type === action.payload.type) {
        return state;
      }

      return {
        ...state,
        activeCohortBuilderItem: {
          ...activeCohortBuilderItem,
          type: action.payload.type,
          conceptFilters: [],
          numericFilters: [],
          relatedEntityFilters: [],
          uniquenessFilters: [],
        },
      };
    },

    // Set the active cohort builder item derived event type
    setActiveCohortBuilderItemDerivedEventType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: DerivedEventTypeLegacy;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const hasTypeSelectionChanged = state.activeCohortBuilderItem?.type !== action.payload.type;

      // if the name is set and the type has changed, reset the events and pass through the runtime
      if (activeCohortBuilderItem.name && hasTypeSelectionChanged) {
        const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
          ...activeCohortBuilderItem,
          type: action.payload.type,
          // reset the events when the type changes, otherwise keep the existing events
          eventFilters: hasTypeSelectionChanged
            ? []
            : state.activeCohortBuilderItem?.eventFilters ?? [],
          // reset the temporal constraints when the type changes, otherwise keep the existing temporal constraints
          temporalConstraints: hasTypeSelectionChanged
            ? []
            : state.activeCohortBuilderItem?.temporalConstraints ?? [],
          uniquenessFilters: hasTypeSelectionChanged
            ? []
            : state.activeCohortBuilderItem?.uniquenessFilters ?? [],
          // inputFieldConfigMap: getNextInputFieldItemsMapForDerivedEvent(events),
        };

        const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
          state.cohortBuilderWorkflow?.modal,
          state.cohortDefinition,
          currentActiveCohortBuilderItem,
          {
            activeCohortBuilderItem: currentActiveCohortBuilderItem,
            conceptGroups: state.conceptGroups,
            currentCohortDefinition: state.cohortDefinition,
            derivedEventType: action.payload.type,
            featureFlags: state.featureFlags,
          }
        );

        return {
          ...state,
          activeCohortBuilderItem: nextActiveCohortBuilderItem,
        };
      }

      // only update the type if the name is not set
      return {
        ...state,
        activeCohortBuilderItem: {
          ...activeCohortBuilderItem,
          type: action.payload.type,
        },
      };
    },

    // Set the active cohort builder item index date type
    setActiveCohortBuilderItemIndexDateType(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: IndexDateTypeLegacy;
      }>
    ) {
      const _activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (_activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      // Do nothing if the type is the same
      if (_activeCohortBuilderItem.type === action.payload.type) {
        return state;
      }

      const activeCohortBuilderItem: ActiveIndexDateItemLegacy = {
        ..._activeCohortBuilderItem,
        type: action.payload.type,
        indexDateValue: undefined,
        indexDateRangeValue: undefined,
        indexDateEvent: undefined,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        activeCohortBuilderItem,
        {
          activeCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: undefined,
          featureFlags: state.featureFlags,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    setActiveCohortBuilderItemIndexDateValue(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        id: string;
        type: IndexDateTypeLegacy;
        indexDateValue?: string;
        indexDateEvent?: IndexDateEvent;
        indexDateRangeValue?: IndexDateRangeLegacy;
      }>
    ) {
      const _activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (_activeCohortBuilderItem === undefined) {
        throw new Error(
          'setActiveCohortBuilderItemIndexDateValue: activeCohortBuilderItem is undefined'
        );
      }

      if (_activeCohortBuilderItem.id !== action.payload.id) {
        throw new Error(
          `setActiveCohortBuilderItemIndexDateValue: activeCohortBuilderItem id does not match\nActive id: ${_activeCohortBuilderItem.id}\nPayload id: ${action.payload.id}`
        );
      }

      if (_activeCohortBuilderItem.type !== action.payload.type) {
        throw new Error(
          `setActiveCohortBuilderItemIndexDateValue: activeCohortBuilderItem type does not match\nActive type: ${_activeCohortBuilderItem.type}\nPayload type: ${action.payload.type}`
        );
      }

      const activeCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ..._activeCohortBuilderItem,
        indexDateValue: action.payload.indexDateValue,
        indexDateEvent: action.payload.indexDateEvent,
        indexDateRangeValue: action.payload.indexDateRangeValue,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        activeCohortBuilderItem,
        {
          activeCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: undefined,
          featureFlags: state.featureFlags,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    addActiveCohortBuilderEventForDerivedEventType(
      state: CohortBuilderSlice,
      action: PayloadAction<DerivedEventTypeLegacy>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        eventFilters: [
          ...activeCohortBuilderItem.eventFilters,
          getNewCohortBuilderEvent(action.payload),
        ],
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: action.payload,
          featureFlags: state.featureFlags,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },
    setActiveCohortBuilderEventInputFieldValue(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        derivedEventFilterId: string; // the id of the derived event being updated
        derivedEventType: DerivedEventTypeLegacy;
        derivedEventFieldType: DerivedEventInputFieldTypeLegacy;
        value: string;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const eventNameChanged =
        action.payload.derivedEventFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME &&
        activeCohortBuilderItem.name !== action.payload.value;
      const derivedEventInputFieldType = action.payload.derivedEventFieldType;

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        eventFilters: activeCohortBuilderItem.eventFilters.map((event) => {
          if (event.id === action.payload.derivedEventFilterId) {
            let nextEventDateAttributeValue = event.eventDateAttribute.value;
            let nextPositionValue =
              action.payload.derivedEventType === DerivedEventTypeLegacy.INSTANCE
                ? {
                    id: event.position?.id ?? '',
                    value:
                      derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_POSITION
                        ? action.payload.value
                        : event.position?.value ?? '',
                  }
                : undefined;
            let nextIndexValue =
              action.payload.derivedEventType === DerivedEventTypeLegacy.INSTANCE
                ? {
                    id: event.index?.id ?? '',
                    value:
                      derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_INDEX
                        ? action.payload.value
                        : event.index?.value ?? '',
                  }
                : undefined;
            if (
              derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_DATE_ATTRIBUTE
            ) {
              nextEventDateAttributeValue = action.payload.value;
            }
            if (
              derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME &&
              eventNameChanged
            ) {
              nextEventDateAttributeValue = '';
              nextPositionValue = nextPositionValue && { ...nextPositionValue, value: '' };
              nextIndexValue = nextIndexValue && { ...nextIndexValue, value: '' };
            }
            if (nextPositionValue?.value !== 'INDEX') {
              nextIndexValue = undefined;
            }

            return {
              ...event,
              eventName: {
                ...event.eventName,
                value:
                  derivedEventInputFieldType === DerivedEventInputFieldTypeLegacy.EVENT_NAME
                    ? action.payload.value
                    : event.eventName.value,
              },
              eventDateAttribute: {
                ...event.eventDateAttribute,
                value: nextEventDateAttributeValue,
              },
              isSelected:
                action.payload.derivedEventType === DerivedEventTypeLegacy.SEQUENCE
                  ? {
                      id: event.isSelected?.id ?? '',
                      value:
                        derivedEventInputFieldType ===
                          DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED &&
                        action.payload.value === 'true' // only allow switching to true, we should never uncheck but instead select a different event to be true
                          ? action.payload.value
                          : event.isSelected?.value ?? 'false',
                    }
                  : undefined,
              position: nextPositionValue,
              index: nextIndexValue,
            };
          }
          return {
            ...event,
            isSelected:
              action.payload.derivedEventType === DerivedEventTypeLegacy.SEQUENCE
                ? {
                    id: event.isSelected?.id ?? '',
                    value:
                      derivedEventInputFieldType ===
                        DerivedEventInputFieldTypeLegacy.EVENT_IS_SELECTED &&
                      action.payload.value === 'true'
                        ? 'false'
                        : event.isSelected?.value ?? 'false',
                  }
                : undefined,
          };
        }),
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: action.payload.derivedEventType,
          featureFlags: state.featureFlags,
        }
      );

      state.activeCohortBuilderItem = nextActiveCohortBuilderItem;
    },
    deleteActiveCohortBuilderEvent(state: CohortBuilderSlice, action: PayloadAction<string>) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        eventFilters: activeCohortBuilderItem.eventFilters.filter(
          (event) => event.id !== action.payload
        ),
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: activeCohortBuilderItem.type as DerivedEventTypeLegacy,
          featureFlags: state.featureFlags,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    setActiveCohortBuilderItemAnyOrder(state: CohortBuilderSlice, action: PayloadAction<boolean>) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        anyOrder: action.payload,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          derivedEventType: activeCohortBuilderItem.type as DerivedEventTypeLegacy,
          featureFlags: state.featureFlags,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    /** When a user clicks to add a new filter */
    addActiveCohortBuilderItemFilter(
      state: CohortBuilderSlice,
      action: PayloadAction<FilterTypeLegacy>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const newFilter: FilterLegacy = {
        id: uuidv4(),
        inputFields: [],
      };

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        conceptFilters:
          action.payload === FilterTypeLegacy.CONCEPT
            ? [...activeCohortBuilderItem.conceptFilters, newFilter]
            : activeCohortBuilderItem.conceptFilters,
        numericFilters:
          action.payload === FilterTypeLegacy.NUMERIC
            ? [...activeCohortBuilderItem.numericFilters, newFilter]
            : activeCohortBuilderItem.numericFilters,
        relatedEntityFilters:
          action.payload === FilterTypeLegacy.RELATED_ENTITY
            ? [...(activeCohortBuilderItem.relatedEntityFilters ?? []), newFilter]
            : activeCohortBuilderItem.relatedEntityFilters,
        uniquenessFilters:
          action.payload === FilterTypeLegacy.UNIQUENESS
            ? [...(activeCohortBuilderItem.uniquenessFilters ?? []), newFilter]
            : activeCohortBuilderItem.uniquenessFilters,
        temporalConstraints:
          action.payload === FilterTypeLegacy.TEMPORAL
            ? [...activeCohortBuilderItem.temporalConstraints, newFilter]
            : activeCohortBuilderItem.temporalConstraints,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          conceptGroups: state.conceptGroups,
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          currentCohortDefinition: state.cohortDefinition,
          featureFlags: state.featureFlags,
          derivedEventType:
            action.payload === FilterTypeLegacy.TEMPORAL
              ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    /** When a user modifies a filter's input field value */
    setActiveCohortBuilderItemFilterInputFieldValue(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        filterId: string;
        filterType: FilterTypeLegacy;
        inputFieldId: string;
        value: string;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }
      const filterType = action.payload.filterType;
      const filterId = action.payload.filterId;
      const inputFieldId = action.payload.inputFieldId;
      const value = action.payload.value;

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        conceptFilters:
          filterType === FilterTypeLegacy.CONCEPT
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.conceptFilters,
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.conceptFilters,
        numericFilters:
          filterType === FilterTypeLegacy.NUMERIC
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.numericFilters,
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.numericFilters,
        relatedEntityFilters:
          filterType === FilterTypeLegacy.RELATED_ENTITY
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.relatedEntityFilters ?? [],
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.relatedEntityFilters,
        temporalConstraints:
          filterType === FilterTypeLegacy.TEMPORAL
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.temporalConstraints,
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.temporalConstraints,
        uniquenessFilters:
          filterType === FilterTypeLegacy.UNIQUENESS
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.uniquenessFilters ?? [],
                filterId,
                inputFieldId,
                value
              )
            : activeCohortBuilderItem.uniquenessFilters,
        countFilter:
          filterType === FilterTypeLegacy.COUNT
            ? getNextFilterForInputFieldValueUpdate(
                activeCohortBuilderItem.countFilter ? [activeCohortBuilderItem.countFilter] : [],
                filterId,
                inputFieldId,
                value
              )[0]
            : activeCohortBuilderItem.countFilter,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          conceptGroups: state.conceptGroups,
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          currentCohortDefinition: state.cohortDefinition,
          featureFlags: state.featureFlags,
          derivedEventType:
            action.payload.filterType === FilterTypeLegacy.TEMPORAL
              ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );

      // Fix: mutate the draft directly, do not return a new object
      state.activeCohortBuilderItem = nextActiveCohortBuilderItem;
    },

    /** When a user modifies the time window */
    setActiveCohortBuilderItemTimeWindow(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        timeWindow: TimeWindow;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        timeWindow: action.payload.timeWindow,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          conceptGroups: state.conceptGroups,
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          currentCohortDefinition: state.cohortDefinition,
          featureFlags: state.featureFlags,
          derivedEventType: undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },

    /** When a user deletes a filter */
    deleteActiveCohortBuilderItemFilter(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        filterId: string;
        filterType: FilterTypeLegacy;
      }>
    ) {
      const activeCohortBuilderItem = state.activeCohortBuilderItem;
      if (activeCohortBuilderItem === undefined) {
        throw new Error('activeCohortBuilderItem is undefined');
      }

      const currentActiveCohortBuilderItem: ActiveCohortBuilderItemLegacy = {
        ...activeCohortBuilderItem,
        conceptFilters:
          action.payload.filterType === FilterTypeLegacy.CONCEPT
            ? activeCohortBuilderItem.conceptFilters.filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.conceptFilters,
        numericFilters:
          action.payload.filterType === FilterTypeLegacy.NUMERIC
            ? activeCohortBuilderItem.numericFilters.filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.numericFilters,
        relatedEntityFilters:
          action.payload.filterType === FilterTypeLegacy.RELATED_ENTITY
            ? (activeCohortBuilderItem.relatedEntityFilters ?? []).filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.relatedEntityFilters,
        temporalConstraints:
          action.payload.filterType === FilterTypeLegacy.TEMPORAL
            ? activeCohortBuilderItem.temporalConstraints.filter(
                (temporalConstraint) => temporalConstraint.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.temporalConstraints,
        uniquenessFilters:
          action.payload.filterType === FilterTypeLegacy.UNIQUENESS
            ? (activeCohortBuilderItem.uniquenessFilters ?? []).filter(
                (filter) => filter.id !== action.payload.filterId
              )
            : activeCohortBuilderItem.uniquenessFilters,
      };

      const nextActiveCohortBuilderItem = recursiveCheckNextActiveCohortBuilderItemState(
        state.cohortBuilderWorkflow?.modal,
        state.cohortDefinition,
        currentActiveCohortBuilderItem,
        {
          activeCohortBuilderItem: currentActiveCohortBuilderItem,
          conceptGroups: state.conceptGroups,
          currentCohortDefinition: state.cohortDefinition,
          featureFlags: state.featureFlags,
          derivedEventType:
            action.payload.filterType === FilterTypeLegacy.TEMPORAL
              ? (activeCohortBuilderItem.type as DerivedEventTypeLegacy)
              : undefined,
        }
      );

      return {
        ...state,
        activeCohortBuilderItem: nextActiveCohortBuilderItem,
      };
    },
    // setLoggedInUserIsCohortOwner(state: CohortBuilderSlice, action: PayloadAction<boolean>) {
    //   return {
    //     ...state,
    //     isLoggedInUserCohortOwner: action.payload,
    //   };
    // },
    setCohortUpdatedByBuilder(state: CohortBuilderSlice, action: PayloadAction<boolean>) {
      return {
        ...state,
        isCohortUpdatedByBuilder: action.payload,
      };
    },

    setCohortBuilderFeatureFlags(
      state: CohortBuilderSlice,
      action: PayloadAction<Record<string, string | boolean>>
    ) {
      return {
        ...state,
        featureFlags: {
          ...state.featureFlags,
          ...action.payload,
        },
      };
    },

    setConceptGroupsConfidence(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        conceptGroupsConfidence: ConceptGroupsConfidence;
        clearOld?: boolean;
      }>
    ) {
      if (action.payload.clearOld === false) {
        return {
          ...state,
          conceptGroupsConfidence: action.payload.conceptGroupsConfidence,
        };
      }

      // If clearOld is set then only keep concept groups confidence items for events or criteria
      // that have been changed or newly added
      // We can compare the conceptGroupsConfidence with the old and new but that will not give correct result
      // Firstly the ids will change between old and new and secondly we want to clear the results everytime
      const newConfidence = action.payload.conceptGroupsConfidence.filter((item) => {
        const changedSimpleEvent = state.cohortDefinition?.simpleEvents.find(
          (s) =>
            s.id === item.eventId &&
            (s.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW ||
              s.changeStatus === CohortBuilderItemChangeStatusLegacy.EDITED)
        );
        if (changedSimpleEvent) return true;

        const changedSubjectVariable = state.cohortDefinition?.subjectVariables.find(
          (v) =>
            v.id === item.eventId &&
            (v.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW ||
              v.changeStatus === CohortBuilderItemChangeStatusLegacy.EDITED)
        );
        if (changedSubjectVariable) return true;

        return false;
      });

      return {
        ...state,
        conceptGroupsConfidence: newConfidence,
      };
    },

    markConceptGroupAsGenerated(
      state: CohortBuilderSlice,
      action: PayloadAction<{ conceptGroupName: string }>
    ) {
      return {
        ...state,
        conceptGroupsConfidence: state.conceptGroupsConfidence?.map((item) => ({
          ...item,
          conceptGroups: item.conceptGroups?.map((conceptGroup) =>
            conceptGroup.span.toLowerCase().trim() ===
            action.payload.conceptGroupName.toLowerCase().trim()
              ? { ...conceptGroup, generated: true }
              : conceptGroup
          ),
        })),
      };
    },

    /**
     * Set concept group selections (unified structure with domain context)
     *
     * The backend sends ALL selections (including old ones with reported=true),
     * so we simply replace the entire array. Filtering of reported selections
     * happens in the selector (selectConceptGroupSelectionsWithIssues).
     */
    setConceptGroupSelections(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        conceptGroupSelections: ConceptGroupSelection[];
      }>
    ) {
      return setConceptGroupSelectionsReducer(state, action);
    },

    /**
     * Optimistically update concept group selection reported status
     * Used when user clicks suggested action to create concept group
     * This optimistic update will be synced from backend after creation response or on reload
     */
    optimisticCgsReportedUpdate(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        filterKey: string;
      }>
    ) {
      return optimisticCgsReportedUpdateReducer(state, action);
    },

    /**
     * Mark concept group selections as reported/reviewed
     * Used when user saves a container or makes a follow-up request
     * Supports marking by filterKeys array, containerName, or markAll flag
     */
    markConceptGroupSelectionsAsReported(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        filterKeys?: string[];
        containerName?: string;
        markAll?: boolean;
      }>
    ) {
      return markConceptGroupSelectionsAsReportedReducer(state, action);
    },

    /**
     * Queue event modals for concept groups
     * Supports selections with filter context for multiple filters
     */
    queueEventModalsForConceptGroups(
      state: CohortBuilderSlice,
      action: PayloadAction<{
        selections: QueuedEventModalSelection[];
        conceptGroupId?: string;
      }>
    ) {
      return {
        ...state,
        queuedEventModals: action.payload.selections,
        queuedEventModalsConceptGroupId: action.payload.conceptGroupId,
      };
    },

    clearQueuedEventModals(state: CohortBuilderSlice) {
      return {
        ...state,
        queuedEventModals: undefined,
        queuedEventModalsConceptGroupId: undefined,
      };
    },

    clearConceptGroupsConfidence(
      state: CohortBuilderSlice,
      action: PayloadAction<{ id: string } | undefined>
    ) {
      if (!action.payload || !action.payload?.id) {
        return {
          ...state,
          conceptGroupsConfidence: [],
        };
      }

      return {
        ...state,
        conceptGroupsConfidence: state.conceptGroupsConfidence?.filter(
          (item) => item.eventId !== action.payload?.id
        ),
      };
    },

    /** Staged changes actions for undo functionality */
    setStagedChanges(state: CohortBuilderSlice, action: PayloadAction<boolean>) {
      return {
        ...state,
        hasStagedChanges: action.payload,
      };
    },

    clearStagedChanges(state: CohortBuilderSlice) {
      return {
        ...state,
        hasStagedChanges: false,
      };
    },

    /** Clear changeStatus and changedBy from all cohort definition items */
    clearItemChangeStatuses(state: CohortBuilderSlice) {
      if (!state.cohortDefinition) {
        return state;
      }

      return {
        ...state,
        cohortDefinition: {
          ...state.cohortDefinition,
          indexDate: state.cohortDefinition.indexDate
            ? {
                ...state.cohortDefinition.indexDate,
                changeStatus: undefined,
                changedBy: undefined,
              }
            : undefined,
          subjectVariables: state.cohortDefinition.subjectVariables?.map((sv) => ({
            ...sv,
            changeStatus: undefined,
            changedBy: undefined,
          })),
          simpleEvents: state.cohortDefinition.simpleEvents?.map((se) => ({
            ...se,
            changeStatus: undefined,
            changedBy: undefined,
          })),
          derivedEvents: state.cohortDefinition.derivedEvents?.map((de) => ({
            ...de,
            changeStatus: undefined,
            changedBy: undefined,
          })),
          analyticalVariables: state.cohortDefinition.analyticalVariables?.map((av) => ({
            ...av,
            changeStatus: undefined,
            changedBy: undefined,
          })),
          criteria: state.cohortDefinition.criteria?.map((c) =>
            'changeStatus' in c || 'changedBy' in c
              ? { ...c, changeStatus: undefined, changedBy: undefined }
              : c
          ),
          studyPeriodRestrictions: state.cohortDefinition.studyPeriodRestrictions
            ? {
                ...state.cohortDefinition.studyPeriodRestrictions,
                changeStatus: undefined,
                changedBy: undefined,
              }
            : undefined,
        },
        // Clear concept group confidence warnings when clearing change statuses
        // This ensures warning indicators are removed after explicit save
        conceptGroupsConfidence: [],
      };
    },
  },

  /**
   * The extra reducers below perform automatic state updates when certain actions are dispatched.
   */
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) =>
        action.type === cohortBuilderSlice.actions['setInitialCohortBuilderSliceState'].type,
      (state) => {
        const { cohortBuilderItemNamesToIdsMap, cohortBuilderItemIdsToNamesMap } =
          getCohortBuilderItemNameAndIdMaps(state.cohortDefinition);
        return {
          ...state,
          cohortBuilderWorkflow: {
            // activeStep: DEFAULT_COHORT_BUILDER_STEP,
            hasUnsavedChanged: false,
            isProcessEnabled: state.cohortDefinition
              ? validateCohortDefinitionForProcessing(state.cohortDefinition)
              : false,
            modal: undefined,
            // actionModal: state.cohortBuilderWorkflow?.actionModal,
            inUseCohortBuilderItemsMap: getInUseCohortBuilderItemsMap(
              state.cohortDefinition,
              cohortBuilderItemNamesToIdsMap
            ),
            cohortBuilderItemNamesToIdsMap,
            cohortBuilderItemIdsToNamesMap,
          },
        };
      }
    );
    /** Clear the active cohort builder item state when the item is saved or canceled */
    builder.addMatcher(
      (action) => {
        const actionsToMatch = [
          cohortBuilderSlice.actions['updateCohortBuilderSliceState'].type,
          cohortBuilderSlice.actions['setIndexDate'].type,
          cohortBuilderSlice.actions['deleteIndexDate'].type,
          cohortBuilderSlice.actions['setSubjectVariable'].type,
          cohortBuilderSlice.actions['deleteSubjectVariable'].type,
          cohortBuilderSlice.actions['setSimpleEvent'].type,
          cohortBuilderSlice.actions['deleteSimpleEvent'].type,
          cohortBuilderSlice.actions['setDerivedEvent'].type,
          cohortBuilderSlice.actions['deleteDerivedEvent'].type,
          cohortBuilderSlice.actions['setStudyPeriodRestrictions'].type,
        ];
        return actionsToMatch.includes(action.type);
      },
      (state) => {
        if (state.cohortBuilderWorkflow === undefined) {
          throw new Error('cohortBuilder extraReducer: cohortBuilderWorkflow is undefined');
        }

        if (state.cohortDefinition === undefined) {
          throw new Error('cohortBuilder extraReducer: cohortDefinition is undefined');
        }

        const { cohortBuilderItemNamesToIdsMap, cohortBuilderItemIdsToNamesMap } =
          getCohortBuilderItemNameAndIdMaps(state.cohortDefinition);
        return {
          ...state,
          cohortBuilderWorkflow: {
            // activeStep: state.cohortBuilderWorkflow?.activeStep ?? DEFAULT_COHORT_BUILDER_STEP,
            hasUnsavedChanged: validateCohortDefinitionForSave(state.cohortDefinition),
            isProcessEnabled: validateCohortDefinitionForProcessing(state.cohortDefinition),
            modal: undefined,
            // actionModal: undefined,
            inUseCohortBuilderItemsMap: getInUseCohortBuilderItemsMap(
              state.cohortDefinition,
              cohortBuilderItemNamesToIdsMap
            ),
            cohortBuilderItemNamesToIdsMap,
            cohortBuilderItemIdsToNamesMap,
            viewedCohortBuilderItems: state.cohortBuilderWorkflow?.viewedCohortBuilderItems ?? {},
          },
          activeCohortBuilderItem: undefined,
        };
      }
    );
  },
});

export const {
  /** Set cohort definition */
  resetCohortBuilderSlice,
  setInitialCohortBuilderSliceState,
  updateCohortBuilderSliceState,

  /** Concept group action */
  setConceptGroups,

  /** Index Date Actions */
  setIndexDate,
  deleteIndexDate,

  /** Subject Variable Actions */
  setSubjectVariable,
  deleteSubjectVariable,

  /** Simple Event Actions */
  setSimpleEvent,
  deleteSimpleEvent,

  /** Derived Event Actions */
  setDerivedEvent,
  deleteDerivedEvent,

  /** Study Period Restriction Actions */
  setStudyPeriodRestrictions,

  /** Workflow Actions */
  // setActiveStep,
  setModal,
  clearModal,
  // setActionModal,
  // clearActionModal,
  markItemViewed,

  /** Active Cohort Builder Item Actions */
  // setActiveIndexDateItem,
  // setActiveSubjectsVariableItem,
  setActiveCohortBuilderItemName,
  setActiveCohortBuilderItemSubjectVariableType,
  setActiveCohortBuilderItemSimpleEventType,
  setActiveCohortBuilderItemDerivedEventType,
  setActiveCohortBuilderItemIndexDateType,
  setActiveCohortBuilderItemIndexDateValue,
  setActiveCohortBuilderItemAnyOrder,
  addActiveCohortBuilderItemFilter,
  setActiveCohortBuilderItemFilterInputFieldValue,
  setActiveCohortBuilderItemTimeWindow,
  deleteActiveCohortBuilderItemFilter,
  addActiveCohortBuilderEventForDerivedEventType,
  setActiveCohortBuilderEventInputFieldValue,
  deleteActiveCohortBuilderEvent,
  // setLoggedInUserIsCohortOwner,
  setCohortUpdatedByBuilder,
  setCohortBuilderFeatureFlags,
  setConceptGroupsConfidence,
  setConceptGroupSelections,
  optimisticCgsReportedUpdate,
  markConceptGroupSelectionsAsReported,
  markConceptGroupAsGenerated,
  queueEventModalsForConceptGroups,
  clearQueuedEventModals,
  clearConceptGroupsConfidence,
  setStagedChanges,
  clearStagedChanges,
  clearItemChangeStatuses,
} = cohortBuilderSlice.actions;

declare module '@verantos-dev/app-state-base' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof cohortBuilderSlice> {}
}

// Inject the slice into the root reducer
cohortBuilderSlice.injectInto(rootReducer);

export const selectHasStagedChanges = (state: Partial<CohortBuilderState>) =>
  state[COHORT_BUILDER_SLICE_KEY]?.hasStagedChanges ?? false;
